import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Snackbar,
  FormControl, InputLabel, Select, MenuItem, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  CircularProgress, Divider, Stack, IconButton, Tooltip
} from '@mui/material';
import { SwapHoriz, Refresh, History, PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function TransferenciasBodegas() {
  const [productos, setProductos] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [ultimaTransferencia, setUltimaTransferencia] = useState(null);

  const [productoSel, setProductoSel] = useState(null);
  const [bodegaOrigen, setBodegaOrigen] = useState('');
  const [bodegaDestino, setBodegaDestino] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    cargarProductos();
    cargarBodegas();
    cargarHistorial();
  }, []);

  const cargarProductos = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/productos?tipo=PRODUCTO`, { headers: headers() });
      setProductos(Array.isArray(res.data) ? res.data : []);
    } catch { setProductos([]); }
  };

  const cargarBodegas = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/bodegas`, { headers: headers() });
      setBodegas(res.data?.data || []);
    } catch { setBodegas([]); }
  };

  const cargarHistorial = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/kardex-inventario?tipo=TRANSFERENCIA&limit=30`, { headers: headers() });
      setHistorial(res.data?.data || []);
    } catch { setHistorial([]); }
    setLoading(false);
  };

  const verPDF = (movimientoId) => {
    fetch(`${API_URL}/api/kardex-inventario/movimiento/${movimientoId}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      })
      .catch(() => setSnackbar({ open: true, message: 'Error al abrir PDF', severity: 'error' }));
  };

  const realizarTransferencia = async () => {
    if (!productoSel) return setSnackbar({ open: true, message: 'Seleccione un producto', severity: 'error' });
    if (!bodegaOrigen) return setSnackbar({ open: true, message: 'Seleccione bodega origen', severity: 'error' });
    if (!bodegaDestino) return setSnackbar({ open: true, message: 'Seleccione bodega destino', severity: 'error' });
    if (bodegaOrigen === bodegaDestino) return setSnackbar({ open: true, message: 'Las bodegas deben ser diferentes', severity: 'error' });
    if (!cantidad || parseFloat(cantidad) <= 0) return setSnackbar({ open: true, message: 'Cantidad inválida', severity: 'error' });

    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/kardex-inventario/transferencia`, {
        productoId: productoSel.id, bodegaOrigenId: bodegaOrigen, bodegaDestinoId: bodegaDestino,
        cantidad: parseFloat(cantidad), costoUnitario: parseFloat(productoSel.costo_ultima_compra || 0), observaciones,
      }, { headers: headers() });
      const salidaId = res.data?.data?.salida?.id;
      setUltimaTransferencia(salidaId);
      setSnackbar({ open: true, message: 'Transferencia realizada exitosamente', severity: 'success' });
      setProductoSel(null); setBodegaOrigen(''); setBodegaDestino(''); setCantidad(''); setObservaciones('');
      cargarHistorial();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Error en transferencia', severity: 'error' });
    }
    setSaving(false);
  };

  const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>🔄 Transferencias entre Bodegas</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Mueva productos de una bodega a otra. Se registrará un egreso en la bodega origen y un ingreso en la bodega destino.
        </Typography>

        {/* Producto */}
        <Autocomplete
          options={productos}
          getOptionLabel={(o) => `${o.codigo || 'S/C'} - ${o.nombre} (Stock: ${parseFloat(o.cantidad_stock || 0)})`}
          value={productoSel}
          onChange={(_, v) => setProductoSel(v)}
          renderInput={(params) => <TextField {...params} label="Producto a transferir *" placeholder="Buscar por código o nombre..." />}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          sx={{ mb: 3 }}
        />

        {/* Bodegas origen → destino */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Bodega Origen *</InputLabel>
            <Select value={bodegaOrigen} label="Bodega Origen *" onChange={(e) => setBodegaOrigen(e.target.value)}>
              {bodegas.map(b => <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>)}
            </Select>
          </FormControl>

          <SwapHoriz sx={{ fontSize: 36, color: 'primary.main', flexShrink: 0 }} />

          <FormControl fullWidth>
            <InputLabel>Bodega Destino *</InputLabel>
            <Select value={bodegaDestino} label="Bodega Destino *" onChange={(e) => setBodegaDestino(e.target.value)}>
              {bodegas.filter(b => b.id !== bodegaOrigen).map(b => <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField type="number" label="Cantidad *" value={cantidad} sx={{ minWidth: 130 }}
            onChange={(e) => setCantidad(e.target.value)} inputProps={{ min: 0.01, step: 0.01 }} />
        </Stack>

        {/* Observaciones */}
        <TextField fullWidth label="Observaciones" value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)} multiline rows={2} sx={{ mb: 3 }} />

        {/* Botones */}
        <Stack direction="row" spacing={2}>
          <Button variant="contained" size="large" startIcon={<SwapHoriz />} disabled={saving}
            onClick={realizarTransferencia} sx={{ px: 4, py: 1.5 }}>
            {saving ? 'Procesando...' : 'REALIZAR TRANSFERENCIA'}
          </Button>

          {ultimaTransferencia && (
            <Button variant="outlined" size="large" startIcon={<PictureAsPdf />}
              onClick={() => verPDF(ultimaTransferencia)} color="error" sx={{ px: 3, py: 1.5 }}>
              Ver Comprobante PDF
            </Button>
          )}
        </Stack>

        <Divider sx={{ my: 4 }} />

        {/* Historial */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Historial de Transferencias</Typography>
          <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={cargarHistorial}>Actualizar</Button>
        </Box>

        {loading ? <Box textAlign="center" py={4}><CircularProgress /></Box> : historial.length === 0 ? (
          <Alert severity="info">No hay transferencias registradas.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Fecha</strong></TableCell>
                  <TableCell><strong>Producto</strong></TableCell>
                  <TableCell><strong>Bodega</strong></TableCell>
                  <TableCell align="right"><strong>Cantidad</strong></TableCell>
                  <TableCell><strong>Referencia</strong></TableCell>
                  <TableCell><strong>Observaciones</strong></TableCell>
                  <TableCell align="center"><strong>PDF</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{fmtFecha(m.fecha)}</TableCell>
                    <TableCell>{m.producto?.nombre || m.productoId?.substring(0, 8)}</TableCell>
                    <TableCell>{m.bodega?.nombre || '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{m.cantidad}</TableCell>
                    <TableCell>{m.referencia?.numero || '—'}</TableCell>
                    <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>{m.observaciones || '—'}</Typography></TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver comprobante PDF">
                        <IconButton size="small" color="error" onClick={() => verPDF(m.id)}>
                          <PictureAsPdf fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
